import { Badge, SectionCard } from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import type { OrderDetail } from '@/lib/api-admin/types';

/**
 * 주문상세 "관리자 메모" — 읽기 전용.
 *
 * be-order 계약(feat/admin-order-memo, AdminOrderDetailResponse · V70 order_memo_entries):
 *   GET /api/admin/v1/orders/{orderId} → 고객 상세 필드 그대로 + 아래 두 필드
 *   - adminMemo: string | null — 관리자 메모 한 줄(고도몰 관리자 메모, 수기주문 메모). 상담 이력은 들어 있지 않다.
 *   - memoEntries: [{ kind, writtenAt, writtenBy, content }] — 고객 요청사항·상담 이력, 작성일순, 원문 전체.
 *       kind CUSTOMER_REQUEST(고객 요청) / CONSULT(상담) · writtenAt ISO-8601(UTC) 또는 null ·
 *       writtenBy 작성자 표기(이관은 "godo:관리자번호") 또는 null · content 줄바꿈 포함 원문
 * 관리자 전용 응답에만 있고, 고객 상세(GET /api/v1/orders/{orderId})에는 둘 다 없다.
 * 추가·수정 API 는 아직 없다.
 *
 * 두 필드는 generated OpenAPI 스펙에 아직 없어(codegen lag) OrderDetail 을 로컬 타입으로 확장해 읽는다
 * (EventApplicationInputsSection 과 같은 방식). 둘 다 비면 카드를 숨긴다.
 */
export type OrderMemoKind = 'CUSTOMER_REQUEST' | 'CONSULT';

export interface OrderMemoEntry {
  readonly kind: OrderMemoKind | string;
  readonly writtenAt?: string | null;
  readonly writtenBy?: string | null;
  readonly content?: string | null;
}

type OrderWithMemos = OrderDetail & {
  readonly adminMemo?: string | null;
  readonly memoEntries?: ReadonlyArray<OrderMemoEntry> | null;
};

const hasText = (v: string | null | undefined): v is string => typeof v === 'string' && v.trim() !== '';

const KST = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** UTC ISO → "YYYY-MM-DD HH:MM" (KST). 관리자 PC 시간대와 무관하게 KST 로 보인다. */
export const formatKst = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const part = (type: Intl.DateTimeFormatPartTypes) => KST.formatToParts(d).find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')} ${part('hour')}:${part('minute')}`;
};

interface OrderAdminMemoSectionProps {
  order: OrderDetail;
}

export const OrderAdminMemoSection = ({ order }: OrderAdminMemoSectionProps) => {
  const { t } = useTranslation('orders');
  const { adminMemo, memoEntries } = order as OrderWithMemos;
  const entries = (memoEntries ?? []).filter((e) => hasText(e.content));
  if (!hasText(adminMemo) && entries.length === 0) return null;

  const kindLabel = (kind: string) =>
    kind === 'CUSTOMER_REQUEST'
      ? t('orders.detail.memoKind.CUSTOMER_REQUEST', '고객 요청')
      : kind === 'CONSULT'
        ? t('orders.detail.memoKind.CONSULT', '상담')
        : kind;

  return (
    <SectionCard title={t('orders.detail.adminMemo', '관리자 메모')} data-testid="order-admin-memo">
      {/* 메모·상담 이력에 고객 이름·연락처가 들어 있을 수 있어 Sentry 리플레이에서 가린다. */}
      <div className="space-y-3" data-sentry-mask>
        {hasText(adminMemo) ? (
          <p
            className="whitespace-pre-wrap break-words text-sm text-[color:var(--content-strong)]"
            data-testid="order-admin-memo-text"
          >
            {adminMemo}
          </p>
        ) : null}

        {entries.length > 0 ? (
          <ul className="space-y-2" data-testid="order-memo-entries">
            {entries.map((entry, idx) => {
              const at = formatKst(entry.writtenAt);
              return (
                <li
                  key={`${entry.kind}-${entry.writtenAt ?? ''}-${idx}`}
                  className="rounded-md px-3 py-2"
                  style={{ background: 'var(--surface-1)', border: '1px solid var(--divider-soft)' }}
                  data-testid="order-memo-entry"
                >
                  <div className="mb-1 flex flex-wrap items-center gap-2 text-xs text-[color:var(--content-muted)]">
                    <Badge size="sm" variant={entry.kind === 'CUSTOMER_REQUEST' ? 'info' : 'neutral'}>
                      {kindLabel(entry.kind)}
                    </Badge>
                    {at ? <span className="tabular-nums">{at}</span> : null}
                    {hasText(entry.writtenBy) ? <span>{entry.writtenBy}</span> : null}
                  </div>
                  <p className="whitespace-pre-wrap break-words text-sm text-[color:var(--content-strong)]">
                    {entry.content}
                  </p>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    </SectionCard>
  );
};
